import { Alert, Button, MenuItem } from '@mui/material'
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AdminJobEmploymentCancelError from 'errors/AdminJobEmploymentCancelError'
import React from 'react'
import { AdminJobEmploymentCancelForm } from 'types/interfaces'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { isEmpty } from 'lodash'
import FormField from 'components/FormField'
import CheckboxFormField from 'components/CheckboxFormField'
import ErrorList from 'components/ErrorList'
import AdminEmploymentDetailContext from 'pages/AdminEmploymentDetail/context'

interface CancelDialogProps {
  open: boolean
  setOpen: (value: boolean) => void
}

const CancelDialog: React.FC<CancelDialogProps> = ({ open, setOpen }) => {
  const { employment, fetchEmployment } = React.useContext(AdminEmploymentDetailContext)

  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const [form, setForm] = React.useState<AdminJobEmploymentCancelForm>({
    cancel_reason: 'Nurse no longer needed',
    should_cancel_job: false,
  })
  const [error, setError] = React.useState<AdminJobEmploymentCancelError>()
  const [loading, setLoading] = React.useState<boolean>(false)

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(update(form, { [name]: { $set: value } }))
    if (error) setError(update(error, { [name]: { $set: [] } }))
  }, [error, form])

  const submit = async () => {
    try {
      setLoading(true)
      await api.cancelJobEmploymentByAdmin(employment.id, form)
      await fetchEmployment()
      setOpen(false)
      showAlert('success', form.should_cancel_job ? 'Match and job cancelled' : 'Match cancelled')
    } catch (e) {
      if (
        e instanceof AdminJobEmploymentCancelError &&
        !(isEmpty(e.schema) && isEmpty(e.cancel_reason) && isEmpty(e.should_cancel_job))
      ) {
        setError(e)
      } else {
        console.error(e)
        showAlert('error', 'Failed to cancel the match')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Cancel as Admin</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormField
          select
          sx={{ marginTop: 1 }}
          label='Cancel Reason'
          value={form.cancel_reason}
          onChange={(e) => updateForm('cancel_reason', e.target.value)}
          errors={error?.cancel_reason}
        >
          <MenuItem value='Nurse no longer needed'>Nurse no longer needed</MenuItem>
          <MenuItem value='Found a nurse elsewhere'>Found a nurse elsewhere</MenuItem>
          <MenuItem value='Others'>Others</MenuItem>
        </FormField>
        <CheckboxFormField
          label='Also cancel the job'
          helperText='If unchecked, the job stays open and other nurses may take it.'
          onChange={(e) => updateForm('should_cancel_job', e.target.checked)}
          checked={form.should_cancel_job ?? false}
          errors={error?.should_cancel_job}
        />
        <Alert severity='warning'>
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            <li>Fees will be set to zero, but you can change the fee afterwards in this page.</li>
            <li>No notifications will be sent to the nurse / practice, please do so manually.</li>
            <li>If "Also cancel the job" is checked, the backend will reject your request if another nurse has taken the job (e.g. multiple headcounts).</li>
          </ul>
        </Alert>
        {!isEmpty(error?.schema) ? (
          <Alert severity='error'><ErrorList errors={error?.schema} /></Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button variant='contained' onClick={submit} disabled={loading}>
          { loading ? 'Please Wait ...' : 'Submit' }
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CancelDialog
